import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { publicUrl } from "@/lib/storage";
import FavoriteButton from "@/components/FavoriteButton";

type Product = {
  id: string;
  brand_name: string;
  product_name: string;
  category: string | null;
  price: string | null;
  product_url: string | null;
  quote: string | null;
  image_path: string | null;
  image_paths: string[] | null;
  is_house_brand: boolean | null;
  athleteNames: string[];
};

const Shop = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [pRes, paRes, aRes] = await Promise.all([
        supabase.from("brand_products").select("*").eq("published", true).order("sort_order"),
        supabase.from("product_athletes").select("product_id, athlete_id"),
        supabase.from("athletes").select("id, name").eq("published", true),
      ]);
      const athleteNameById: Record<string, string> = {};
      (aRes.data ?? []).forEach((a: any) => { athleteNameById[a.id] = a.name; });
      const links: Record<string, string[]> = {};
      ((paRes.data as any) ?? []).forEach((r: any) => { (links[r.product_id] ||= []).push(r.athlete_id); });
      const list: Product[] = ((pRes.data as any) ?? []).map((p: any) => ({
        ...p,
        athleteNames: (links[p.id] ?? (p.athlete_id ? [p.athlete_id] : []))
          .map((id: string) => athleteNameById[id])
          .filter(Boolean),
      }));
      setProducts(list);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return products;
    return products.filter((p) => {
      const hay = [
        p.product_name,
        p.brand_name,
        p.category ?? "",
        ...(p.athleteNames ?? []),
      ].join(" ").toLowerCase();
      return hay.includes(s);
    });
  }, [products, q]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container py-16">
        <header className="mb-10 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Shop</span>
          <h1 className="text-5xl md:text-6xl mt-3">Wear what they wear.</h1>
          <p className="text-muted-foreground mt-3">
            Every product is championed by an athlete on our roster.
          </p>
        </header>

        <div className="mb-8 max-w-md">
          <Input
            type="search"
            placeholder="Search by product, brand, or athlete…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground">No products match your search.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((p) => {
              const img = publicUrl(p.image_path);
              const galleryCount = (p.image_paths ?? []).length;
              const firsts = p.athleteNames.map((n) => n.split(" ")[0]);
              const via =
                firsts.length === 0
                  ? ""
                  : firsts.length === 1
                  ? `via ${firsts[0]}`
                  : firsts.length === 2
                  ? `via ${firsts[0]} & ${firsts[1]}`
                  : `via ${firsts[0]} + ${firsts.length - 1} others`;
              return (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group relative block bg-card text-foreground rounded-2xl p-5 shadow-card hover:shadow-bold transition-all"
                >
                  <div className="absolute top-3 right-3 z-10">
                    <FavoriteButton kind="product" id={p.id} size="sm" />
                  </div>
                  <div className="relative aspect-square rounded-xl bg-muted mb-4 overflow-hidden flex items-center justify-center font-display text-4xl text-primary">
                    {img ? (
                      <img src={img} alt={p.product_name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <span>{p.brand_name.charAt(0)}</span>
                    )}
                    {p.is_house_brand && (
                      <span className="absolute top-2 left-2 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
                        Official
                      </span>
                    )}
                    {galleryCount > 0 && (
                      <span className="absolute bottom-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-background/80 text-foreground">
                        +{galleryCount}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {p.brand_name}
                    {p.category ? ` · ${p.category}` : ""}
                  </p>
                  <p className="font-semibold mt-1 text-sm leading-tight">{p.product_name}</p>
                  {p.quote && (
                    <p className="serif italic text-xs text-muted-foreground mt-2 line-clamp-2">“{p.quote}”</p>
                  )}
                  <div className="flex justify-between items-baseline mt-3">
                    <span className="font-display text-lg">{p.price}</span>
                    <span className="text-xs text-muted-foreground">{via}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Shop;
